import AnimatedSprite, {
  AnimatedSpritePlay,
} from "components/Media/AnimatedSprite";
import {
  ProductAAEHUDFactoryType,
  ProductBundleType,
  ProductType,
  ReplicaType,
  ThumbnailSize,
} from "types";
import styled, { css, keyframes } from "styled-components";

const Background = styled.div<{ isNew?: boolean }>`
  --mix-dark: 0.9; // 0-1
  --mix-dark-on-hover: 0.85; // 0-1

  --opacity: 0.8;
  --opacity-on-hover: 0.8;

  position: absolute;
  width: 70%;
  height: 60%;
  top: 20%;
  background: ${(props) => props.theme.colors.white};
  opacity: var(--opacity);
  transition: all 0.2s ease;

  &:after {
    content: " ";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${(props) => props.theme.colors.black};
    opacity: var(--mix-dark);
    transition: all 0.2s ease;
  }

  ${(props) =>
    props.isNew &&
    css`
      --mix-dark: 0.8; // 0-1
      --mix-dark-on-hover: 0.7; // 0-1
    `}
`;

const Thumbnail = styled.div<{ size?: ThumbnailSize; wide?: boolean }>`
  position: absolute;
  width: 90%;
  height: 80%;
  top: 0;

  ${(props) =>
    props.size === ThumbnailSize.L &&
    css`
      width: 110%;
      height: 100%;
      top: -7%;
    `}

  --shadow-blur: 1rem;
  --shadow-offset-y: 1rem;
  --saturate: 0.8;
  filter: drop-shadow(
      0 var(--shadow-offset-y) var(--shadow-blur)
        ${(props) => props.theme.colors.black}88
    )
    saturate(var(--saturate));
  transition: all 0.2s ease;

  & > img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const BundleThumbnail = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const BundleThumbnailSize = styled.div`
  opacity: 0.5;
  pointer-events: none !important;
`;

const BundleSubThumbnail = styled.div<{
  index: number;
  zIndex: number;
  stepXPercentage: number;
  stepScalePercentage: number;
}>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  ${(props) => css`
    filter: drop-shadow(0 0 8px ${props.theme.colors.black});
    z-index: ${props.zIndex};
    transform: translateX(calc(${`${props.index} * ${props.stepXPercentage}%`}))
      scale(${props.stepScalePercentage}%);
  `}
`;

const ThumbnailInfoWrapper = styled.div`
  position: absolute;
  top: 60%;
  left: 0;
  right: 0;

  pointer-events: none;
  border-radius: 2px;
  padding: 10px 10px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ThumbnailInfo = styled.div`
  padding: 10px 10px;
  color: ${(props) => props.theme.colors.white};
  background: ${(props) => props.theme.colors.black}AA;
  border-radius: 4px;
  backdrop-filter: blur(4px);
  opacity: 1;

  font-size: 14px;
  font-weight: 500;
  text-align: center;
  white-space: pre-wrap;

  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ThumbnailInfoVersion = styled.div`
  font-size: 14px;
`;

const ThumbnailInfoYear = styled.div`
  font-size: 12px;
  opacity: 0.6;
`;

const ThumbnailInfoLatest = styled.div`
  font-size: 10px;
  opacity: 0.6;
`;

const Title = styled.div`
  position: absolute;
  top: 82%;
  width: 85%;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  text-align: center;
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  white-space: pre-wrap;
  line-height: 14px;

  --letterSpacing: 0.1em;
  letter-spacing: var(--letterSpacing);
  text-indent: var(--letterSpacing);

  color: ${(props) => props.theme.colors.white};
`;
const Name = styled.div`
  opacity: 0.7;
`;

const NameSecondary = styled.div`
  opacity: 0.7;
  font-size: 0.9em;
`;

const NameTertiary = styled.div`
  opacity: 0.7;
  font-size: 0.9em;
  font-weight: 700;
`;

const Category = styled.div`
  font-size: 0.8em;
  margin-top: 5px;
  opacity: 0.4;
  min-height: 9px;
`;

const LabelsTop = styled.div`
  position: absolute;
  z-index: 1;
  top: 15%;
  left: 15%;
  border-radius: 2px;
  overflow: hidden;
  background: ${(props) => props.theme.colors.black};
  box-shadow: 6px 6px 8px ${(props) => props.theme.colors.black}88;

  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const LabelsBottom = styled.div`
  position: absolute;
  z-index: 1;
  bottom: 35%;
  right: 12%;

  border-radius: 2px;
  overflow: hidden;
  background: ${(props) => props.theme.colors.black};
  box-shadow: 6px 6px 8px ${(props) => props.theme.colors.black}88;

  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const LabelAnim = keyframes`
  0% { 
    opacity: 1;
  }
  50% { 
    opacity: 0.8;
  }
  100% { 
    opacity: 1;
  }
`;
const Label = styled.div<{ isNewVersion?: boolean }>`
  padding: 1px 8px;
  color: ${(props) => props.theme.colors.black};
  background: ${(props) => props.theme.colors.white};

  font-size: 10px;
  font-weight: 800;
  white-space: pre-line;

  transition: all 0.2s ease;

  ${(props) =>
    props.isNewVersion
      ? css`
          font-size: 9px;
          font-weight: 400;
          background: ${(props) => props.theme.colors.black};
          color: ${(props) => props.theme.colors.white};
        `
      : css`
          animation: ${LabelAnim} 1s linear infinite;
        `}

  b {
    font-size: 12px;
    font-weight: 600;
  }
`;

const Wrapper = styled.div<{ thin: boolean; wide: boolean }>`
  background: none;
  border: none;
  outline: none;
  position: relative;
  width: 250px;
  height: 300px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  ${(props) => props.theme.breakpoints.M} {
    // for more crowded intro carousels
    margin: 0 -30px;
  }
  ${(props) => props.theme.breakpoints.SM} {
    width: 200px;
    height: 260px;
  }

  ${({ wide }) =>
    wide &&
    css`
      width: 370px;
    `}

  ${({ thin }) =>
    thin &&
    css`
      width: 190px;
      height: 270px;
    `}

  &:hover {
    ${Background} {
      opacity: var(--opacity-on-hover);

      &:after {
        opacity: var(--mix-dark-on-hover);
      }
    }
    ${Label} {
      filter: brightness(1.2);
      // background: ${(props) => props.theme.colors.white};
    }
  }
`;

const Price = styled.div`
  position: absolute;
  z-index: 1;
  bottom: 20%;
  right: 15%;

  pointer-events: none;

  border-radius: 2px;
  padding: 10px 10px;
  display: flex;
  align-items: center;

  color: ${(props) => props.theme.colors.white};
  background: ${(props) => props.theme.colors.black}DD;

  * {
    font-weight: 500;
  }
  span {
    font-size: 0.9em;
    margin-right: 1px;
  }
`;

const PricePrevious = styled.div`
  --line-color: ${(props) => props.theme.colors.red}AA;

  color: ${(props) => props.theme.colors.red};
  margin-right: 8px;
  background: linear-gradient(
    to left top,
    transparent 47.75%,
    var(--line-color) 49.5%,
    var(--line-color) 50.5%,
    transparent 52.25%
  );
  opacity: 0.6;
`;

type Product =
  | ProductType
  | ProductBundleType
  | ProductAAEHUDFactoryType
  | (ReplicaType & {
      thumbnail: any;
      thumbnailSize?: ThumbnailSize;
    });

const ProductItem = (props: {
  product: Product;
  thumbnailInfo?: {
    version?: string;
    year?: string;
    isLatestVersion?: boolean;
  };
  wide?: boolean;
  thin?: boolean;
  subtitle?: string;
  labels?: string[];
  price?: {
    current: number;
    previous?: number;
  };
  isNew?: boolean;
  isNewVersion?: string;
  is3DPrintable?: boolean;
}) => {
  const renderImg = (product: Product) => {
    // Normalize thumbnail shapes: string, module object ({ default }),
    // sprite object ({ static, sprite, frameCount }) or { src/url }.
    let thumb: string | any = (product as any)?.thumbnail;

    if (thumb && typeof thumb === "object") {
      // Already a SpriteType (has 'static' or 'sprite') — keep as-is
      if ("static" in thumb || "sprite" in thumb) {
        // leave thumb as sprite object
      } else if ("default" in thumb) {
        thumb = (thumb as any).default;
      } else if ("src" in thumb) {
        thumb = (thumb as any).src;
      } else if ("url" in thumb) {
        thumb = (thumb as any).url;
      } else {
        // fallback: stringify to avoid passing an unexpected object
        thumb = undefined;
      }
    }

    if (!thumb) {
      return null;
    }

    return (
      <AnimatedSprite
        src={thumb as any}
        alt={product.name}
        animation={{
          play: AnimatedSpritePlay.infinite,
          frameDuration_Milliseconds: 50,
        }}
        loadingStyle={{
          cssDefault: css`
            transition: all 0.2s ease-out;
          `,
          cssBeforeLoad: css`
            opacity: 0;
            transform: translateY(10px);
          `,
        }}
      />
    );
  };

  const renderThumbnail = () => {
    const bundle = props.product as ProductBundleType;

    if (bundle?.isBundle && !bundle?.thumbnail) {
      return (
        <BundleThumbnail>
          <BundleThumbnailSize>
            {renderImg(bundle.products[0]?.thumbnail)}
          </BundleThumbnailSize>

          {(bundle.generatedThumbnail?.reverseOrder
            ? [...bundle.products].reverse()
            : bundle.products
          )?.map((p, i) => {
            const productCount = bundle.products.length;

            const centerIndex = (productCount - 1) / 2;
            const index = i - centerIndex;

            let zIndex = 100 - Math.abs(index);
            if (productCount === 2 && i === 1) {
              zIndex = 50;
            }

            const scale = bundle.generatedThumbnail?.scale || 1;
            const stepXPercentage = 100 / (productCount + 2);
            const stepScalePercentage =
              scale * (productCount === 2 ? 100 : 100 - Math.abs(index) * 10);

            return (
              <BundleSubThumbnail
                key={i}
                index={index}
                zIndex={zIndex * 2} // *2 to get rid of decimal places
                stepXPercentage={stepXPercentage}
                stepScalePercentage={stepScalePercentage}
              >
                {renderImg(p)}
              </BundleSubThumbnail>
            );
          })}
        </BundleThumbnail>
      );
    }

    return renderImg(props.product);
  };

  //
  // RENDER
  //
  return (
    <Wrapper wide={props.wide} thin={props.thin}>
      <Background isNew={props.isNew} />

      <Thumbnail size={(props.product as any).thumbnailSize} wide={props.wide}>
        {renderThumbnail()}

        {props.thumbnailInfo ? (
          <ThumbnailInfoWrapper>
            <ThumbnailInfo>
              {props.thumbnailInfo.version ? (
                <ThumbnailInfoVersion>
                  {props.thumbnailInfo.version}
                </ThumbnailInfoVersion>
              ) : null}

              {props.thumbnailInfo.year ? (
                <ThumbnailInfoYear>
                  {props.thumbnailInfo.year}
                </ThumbnailInfoYear>
              ) : null}

              {props.thumbnailInfo.isLatestVersion ? (
                <ThumbnailInfoLatest>{"(newest version)"}</ThumbnailInfoLatest>
              ) : null}
            </ThumbnailInfo>
          </ThumbnailInfoWrapper>
        ) : null}
      </Thumbnail>

      <Title>
        {(props.product as any).nameFormatted ? (
          <>
            <Name>{(props.product as any).nameFormatted.primary}</Name>
            {(props.product as any).nameFormatted.secondary ? (
              <NameSecondary>
                {(props.product as any).nameFormatted.secondary}
              </NameSecondary>
            ) : null}
            {(props.product as any).nameFormatted.tertiary ? (
              <NameTertiary>
                {(props.product as any).nameFormatted.tertiary}
              </NameTertiary>
            ) : null}
          </>
        ) : (
          <Name>{props.product.name}</Name>
        )}
        {props.subtitle || props.subtitle === "" ? (
          <Category>{props.subtitle}</Category>
        ) : null}
      </Title>

      <LabelsTop>
        {props.labels?.map((lbl, i) => (
          <Label>{lbl}</Label>
        ))}

        {props.isNew ? <Label>{"New"}</Label> : null}
        {props.isNewVersion ? (
          <Label isNewVersion>
            {`New `}
            <b>{`${props.isNewVersion}`}</b>
          </Label>
        ) : null}
      </LabelsTop>

      <LabelsBottom>
        {props.is3DPrintable ? <Label>{"3D Print"}</Label> : null}
      </LabelsBottom>

      {props.price ? (
        <Price>
          {props.price.previous ? (
            <PricePrevious>
              <span>$</span>
              {props.price.previous}
            </PricePrevious>
          ) : null}
          <div>
            <span>$</span>
            {props.price.current}
          </div>
        </Price>
      ) : null}
    </Wrapper>
  );
};

export default ProductItem;
